import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@chakra-ui/react";
import TableStructure from "../../CommonComponents/TableStructure";
import { getAllAdminUsersData } from "../../services/AdminUsers";
import { setPreview } from "../../redux/actions/previewActions";

const AdminUsersTable = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState("asc");
  const session = useSelector((state) => state.session);
  const dispatch = useDispatch();
  const limit = 50;

  const fetchAdminUsers = async (page, term) => {
    setLoading(true);
    try {
      const result = await getAllAdminUsersData(limit, page, term);
      if (result?.data) {
        setRecords(citationFactor(result?.data?.data || []));
        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);

        if (result.data.page_number && parseInt(result.data.page_number) !== currentPage) {
          setCurrentPage(parseInt(result.data.page_number));
        }
      }
    } catch (error) {
      console.error("Error fetching admin users:", error);
    } finally {
      setLoading(false);
    }
  };

  const citationFactor = (data) => {
    return data?.map((item) => ({
      _id: item?._id,
      username: item?.username,
      email: item?.email,
      last_login_date: item?.last_login_date,
      isActive: item?.isActive,
    }));
  };

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const columns = [
    {
      label: "Username",
      accessor: "username",
    },
    {
      label: "Email",
      accessor: "email",
    },
    {
      label: "Last Login Date",
      accessor: "last_login_date",
      render: (value) => formatDate(value),
    },
    {
      label: "Status",
      accessor: "isActive",
      render: (value) => (
        <Box
          display="inline-flex"
          alignItems="center"
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
          fontWeight="600"
          bg={value === true ? "green.100" : "red.100"}
          color={value === true ? "green.800" : "red.800"}
          border="1px solid"
          borderColor={value === true ? "green.200" : "red.200"}
        >
          {value === true ? "Active" : "Inactive"}
        </Box>
      ),
    },
  ];

  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false;
      setCurrentPage(1);
      setSearchText(searchTerm);
      fetchAdminUsers(1, searchTerm);
    }
  };

  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);

    const sorted = [...records].sort((a, b) =>
      newSortOrder === "asc"
        ? (a.username || "").localeCompare(b.username || "")
        : (b.username || "").localeCompare(a.username || "")
    );

    setRecords(sorted);
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      fetchAdminUsers(currentPage, searchText);
      return;
    }
    fetchAdminUsers(currentPage, searchText);
    dispatch(setPreview(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, dispatch]);

  const handleClear = () => {
    setSearchText('');
    fetchAdminUsers(1, "");
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  return (
    <TableStructure
      isAuthenticated={session?.isAuthenticated}
      handleSearch={handleSearch}
      handleClear={handleClear}
      totalRecords={totalRecords}
      handlePageChange={handlePageChange}
      currentPage={currentPage}
      totalPages={totalPages}
      sortRecords={sortRecords}
      sortOrder={sortOrder}
      loading={loading}
      records={records}
      type="admin-users"
      title="Admin Users"
      addOption
      columns={columns}
    />
  );
};

export default AdminUsersTable;
